import { TYPE_OPTIONS, fmtInputDate } from "../design/domain";

/**
 * 欄位的預設值代號（寫在欄位定義的 def）。
 *
 * 只在「新增」時套用；編輯時的值來自後端，套預設會把使用者原本填的蓋掉。
 *
 * 為什麼不用 new Date().toISOString().slice(0,10)：
 * 那是 UTC 日期。台灣是 UTC+8，凌晨 0~8 點之間會得到「昨天」，
 * 一天有三分之一的時間會填錯。fmtInputDate 是用本地的
 * getFullYear/getMonth/getDate 組出來的，才不會有這個問題。
 */
export const FIELD_DEFAULTS = {
  /** 今天 */
  today: () => fmtInputDate(new Date()),
  /** 今年年底（12/31） */
  yearEnd: () => fmtInputDate(new Date(new Date().getFullYear(), 11, 31)),
};

/**
 * 依欄位定義組出新增表單的初值。
 *
 * 沒有 def 的欄位一律給空字串 —— select/input 才不會在
 * 受控與非受控之間跳動（React 會對此發警告，而且游標會亂掉）。
 *
 * def 不在 FIELD_DEFAULTS 裡時就當成字面值（例如狀態的 def: "待處理"）：
 * 下拉選項本來就是中文字串，為每個固定預設值都加一個代號只是多一層轉手。
 */
export function formInitialValues(fields) {
  return (fields || []).reduce((acc, f) => {
    const make = f.def ? FIELD_DEFAULTS[f.def] : null;
    if (make) acc[f.key] = make();
    else acc[f.key] = f.def || "";
    return acc;
  }, {});
}

/**
 * 新增 / 編輯整頁表單的欄位設定 — 逐字對應 DESIGN 稿 formConfig()（L1040）。
 * names 為可指派人員清單、phaseNames 為本專案的里程碑名稱（皆由呼叫端帶入）。
 *
 * def 代表「新增時的預設值」：
 *   開始日、會議日期一律帶今天（這是最常見的答案，還要再點一次日曆很煩）。
 *   結束日帶年底，是專案／里程碑這種「有期間」的東西的合理起手值。
 *   工項的截止日刻意留空 —— 那是一個承諾，系統不該替使用者決定；
 *   隨手帶一個年底反而會讓一堆工項頂著假期限混在逾期報表裡。
 *
 * ── extra：主要欄位 / 補充資料的分組（設計稿 L1017-L1064、L1124-L1189）──
 *
 * 舊表單把 11 欄一次攤開，使用者第一眼看到的是一面表格而不是一個問題，
 * 結果是「不知道哪些非填不可」→ 隨手亂填 → 資料比空的還沒用。
 * 設計稿只問最少的幾題，其餘收進 <details class="el-disclosure">。
 *
 * extra: true 代表這欄渲染到收合區裡。欄位一個都沒有刪 —— 只是換了位置，
 * 展開就在，送出的 payload 與後端欄位完全不變。
 *
 * ★ 必填欄位進收合區的處理（planner 點名的陷阱，三選一）：
 *   專案代碼 req: true，但設計稿把它放進「補充資料（沒填也可以）」。
 *   照抄就會做出一個「看不見、又擋住送出、<details> 還不會自己展開」的欄位。
 *   **這裡選 (b)**：欄位仍然進 extra、仍然標「必填」，但收合區的說明字
 *   由 EntityForm 依內容自己換 —— 裡面有必填欄位時不寫「沒填也可以」。
 *   選 (b) 而不選 (a)，是因為代碼開頁時就由 /Project/NextCode 預填好了，
 *   放在最前面問等於要使用者確認一個他不該關心的系統編號；
 *   也不選單純的 (c)，因為「送出被擋才展開」是補救，不是說明。
 *   （EntityForm 另外仍實作了 (c) 當保險：驗證失敗時自動展開並聚焦。）
 */
export function formConfig(ctx, names = [], phaseNames = []) {
  const c = {
    items: [
      /* ── 主要欄位：做什麼、誰負責、何時交、哪一種（設計稿 L1128-L1158）── */
      {
        key: "title",
        label: "工項標題",
        type: "text",
        full: true,
        req: true,
        placeholder: "例如：收銀主畫面改為兩欄",
        hint: "以一句話簡述工項內容。",
      },
      /*
       * hint 不要承諾投遞。後端 TaskService.PostTask 只對 project_{id} 群組廣播一則
       * SignalR 通知（NotificationService.NotifyProject），從頭到尾沒有讀 Assignee，
       * 也不寄信、不落地 —— 被指派的人沒開著系統就收不到，事後也補不回來。
       * 同一條規則寫在 pages/Schedule/GoalNew.js:409-412 與 pages/Task/New.js。
       *
       * 「快到期的每日提醒信」是另一回事，別跟這裡混為一談：那是之後某一天
       * 才會寄的彙總信，不是「指派的當下會通知他」。所以 hint 寫成
       * 「指派的當下系統不會通知他」，把時間點講死。
       */
      /*
       * 誰負責改成必填。
       *
       * 沒有負責人的工項在這套系統裡幾乎什麼都不算數：不會出現在任何人的
       * 「個人工項」與「今天」、不進個人績效的母數、時程軸上會被標成
       * 「還沒指派人」的灰條。建了卻沒人認領的工項，實務上等於沒建。
       * 寧可在這裡擋一次，也不要讓它變成一筆沒有人會看到的資料。
       */
      { key: "assignee", label: "負責人", type: "select", req: true, options: names, hint: "工項將計入負責人名下；指派時系統不會即時通知負責人，請另行告知。" },
      /*
       * 截止日是「建議填」，不是必填也不是普通選填。
       *
       * hint 只能寫真的會發生的事，而「真的會發生的事」現在有兩條，所以兩條都要寫：
       *   · 直接的：工項自己也進每日提醒信了（GoalReminderWorker →
       *     GoalReminderService，門檻是 system_setting 的 task.due_soon_days，
       *     在「設定 → 偏好設定」的「工項的到期提醒」改）。快到期或已逾期時，
       *     這一件會列在**負責人**那一封裡 —— 里程碑那一段是寄給專案全體成員，
       *     工項這一段只寄給指派人本人，兩者的收件人不一樣。
       *   · 間接的：這一天會算進所屬里程碑的緩衝
       *     （見 pages/Schedule/rdSummary.js 的 lastEndYmd），里程碑快貼線或超過時，
       *     同一封信的另一段才抓得到。
       * 仍然不寫「會提醒你」那種即時語氣：它是每天一封的彙總，而且只在上班日寄。
       * 另外 AutomationService 的 due_soon / overdue 觸發條件到現在還是沒有執行引擎，
       * 工項的到期提醒與那一套無關，不要因為名字像就把它算進來。
       */
      {
        key: "due_date",
        label: "截止日",
        type: "date",
        rec: true,
        hint: "填寫後，工項即將到期或已逾期時會列入負責人的每日提醒信，並計入所屬里程碑的緩衝。可暫不填寫，之後於工項中補填。",
      },
      /* 所屬里程碑 —— 放在主要欄位（不收進補充資料）：里程碑的完成與進度全靠這個欄位，
         藏起來的話第一次用的人根本找不到。留空代表「未分里程碑」。 */
      {
        key: "phase",
        label: "所屬里程碑",
        type: "select",
        options: phaseNames,
        hint: "里程碑底下的工項全部完成，該里程碑即視為達成。尚未決定可留空，之後於工項中選擇。",
      },
      { key: "type", label: "類型", type: "select", options: TYPE_OPTIONS, hint: "無法判斷時請選擇「工作」。" },

      /*
       * 代碼唯讀。後端發號規則是 issue_key = 「專案代碼-流水號」
       * （TaskService.PostTask），PostTask 會蓋掉送上去的值、PutTask 根本不讀它，
       * 也就是說這個欄位從以前到現在打什麼都沒有作用。
       * 而且 uk_task_issue_key 是唯一索引，開放手改遲早撞號。
       */
      {
        key: "issue_key",
        label: "代碼",
        type: "text",
        ro: true,
        placeholder: "建立後自動產生",
        hint: "由系統依「專案代碼-流水號」自動編號，無法修改。",
      },
      /* 新增時預設「待處理」：新工項本來就是還沒開工，讓使用者每次都再選一次沒有意義 */
      {
        key: "status",
        label: "狀態",
        type: "select",
        options: ["待處理", "進行中", "審核中", "已完成", "已關閉"],
        def: "待處理",
        hint: "新增的工項預設為「待處理」，開始進行後再變更。",
      },
      {
        key: "priority",
        label: "優先級",
        type: "select",
        options: ["緊急", "高", "一般", "低"],
        hint: "未選擇時視為「一般」。",
      },
      {
        key: "start_date",
        label: "開始日",
        type: "date",
        def: "today",
        hint: "預設為今天；如排定於日後開始，請改為該日期。",
      },
      {
        key: "estimated_hours",
        label: "預估工時（小時）",
        type: "number",
        placeholder: "20",
        hint: "請填寫數字；無法預估時可暫不填寫，之後補填。",
      },
      /*
       * rich：這一欄跟工項詳情的「需求說明」是同一個 project_task.description，
       * 那邊用 RichEditor 存的是 HTML。這裡若維持純 textarea，使用者會看到
       * <div><img src="…"> 這種原始碼，而且一存檔就把排版與圖片洗成純文字。
       */
      {
        key: "description",
        label: "詳細說明",
        type: "textarea",
        full: true,
        rich: true,
        placeholder: "可直接貼上圖片",
        hint: "補充背景說明與完成標準，可直接貼上截圖。",
      },
    ],
    phases: [
      { key: "name", label: "里程碑名稱", type: "text", full: true, req: true, placeholder: "例如：第一階段上線" },
      { key: "start_date", label: "開始日", type: "date", def: "today" },
      { key: "end_date", label: "結束日", type: "date", def: "yearEnd" },
      /*
       * 沒有「狀態」欄位是刻意的：里程碑狀態由底下的工項推導
       * （沒工項＝未開始、全部完成／關閉＝已完成、有人開工＝進行中）。
       * 留一個手動下拉只會做出「進度 100% 但狀態待處理」這種卡片 ——
       * 沒人會記得回頭改它。
       */
      { key: "description", label: "說明", type: "textarea", full: true, placeholder: "此階段的目標與完成標準" },
    ],
    meetings: [
      { key: "title", label: "會議主題", type: "text", full: true, req: true, placeholder: "例如：第 3 次需求訪談" },
      { key: "meeting_date", label: "日期", type: "date", def: "today", req: true },
      { key: "time", label: "時間", type: "text", placeholder: "14:00 - 15:00" },
      /* 後端一直存得下也回得出來（ProjectMeeting.Location / Decisions），只是表單沒有這兩格 */
      { key: "location", label: "地點", type: "text", placeholder: "會議室 A／線上（Teams）" },
      /*
       * 出席人員從專案成員挑，不要自己打字。
       * 原本是「以頓號分隔」的文字框 —— 打錯一個字就多一個查無此人的出席者，
       * 而且沒人記得住帳號。值仍是「、」分隔的字串，後端與既有資料都不必動。
       */
      { key: "attendees", label: "出席人員", type: "multiselect", full: true, options: names, placeholder: "＋ 從專案成員加入…" },
      /*
       * 會議記錄用富文本：可以貼截圖、排版，跟工項的需求說明同一套編輯器。
       * 不想打字就用下面的「會議記錄檔案」上傳 DOC —— 兩者都不強制，
       * 開會前先建一筆只有主題與時間的紀錄也是合理的。
       */
      {
        key: "decisions",
        label: "決議事項",
        type: "textarea",
        full: true,
        placeholder: "會議決議內容、負責人與完成期限",
      },
      { key: "content", label: "會議記錄", type: "textarea", full: true, rich: true, placeholder: "討論內容、決議事項與後續負責人" },
      {
        key: "file",
        label: "會議記錄檔案",
        type: "file",
        full: true,
        accept: ".doc,.docx,.pdf,.ppt,.pptx,.xls,.xlsx,.txt,.md,.png,.jpg,.jpeg",
        hint: "可直接上傳會議記錄檔（Word／PDF／簡報，上限 50MB），無須另將內容貼入上方編輯器。",
      },
    ],
    docs: [
      /*
       * 檔案欄位原本不存在 —— 按下「建立」才會偷偷彈出系統的選檔視窗，
       * 表單上完全看不出這件事要傳檔，使用者填完一堆欄位還以為是壞的。
       */
      {
        key: "file",
        label: "檔案",
        type: "file",
        full: true,
        req: true,
        hint: "規格書、設計稿、會議記錄等，上限 50MB。",
      },
      {
        key: "name",
        label: "文件名稱",
        type: "text",
        full: true,
        placeholder: "留空時使用原始檔名",
        hint: "上傳的檔名（例如 doc(3).docx）常不易辨識，建議另行命名；副檔名由系統沿用原始檔案，無須填寫。",
      },
      { key: "category", label: "分類", type: "select", options: ["需求", "設計稿", "會議記錄", "合約", "其他"] },
      /*
       * 沒有「上傳者」欄位是刻意的：後端一律記登入者本人（token 帳號），
       * 給一個下拉只會讓人以為可以掛在別人名下，選了也沒作用。
       */
      { key: "note", label: "說明", type: "textarea", full: true, rich: true, placeholder: "文件內容與閱讀注意事項" },
    ],
    members: [
      { key: "account", label: "選擇帳號", type: "select", options: names, req: true },
      { key: "role", label: "專案角色", type: "select", options: ["專案經理", "開發", "測試", "設計", "觀察者"] },
    ],
    project: [
      /* ── 主要欄位：名字與這個專案要做什麼（設計稿 L1021-L1030）── */
      {
        key: "name",
        label: "專案名稱",
        type: "text",
        full: true,
        req: true,
        placeholder: "例如：新 WEBPOS",
        hint: "請使用團隊慣用的名稱，例如「新 WEBPOS」。",
      },
      { key: "description", label: "專案說明", type: "textarea", full: true, hint: "簡述專案目標，之後可再編輯。", placeholder: "例如：門市 POS 改版，加快結帳速度並自動化對帳" },

      /* ── 補充資料 ── */
      /*
       * 代碼由後端 /Project/NextCode 產生（前綴-年月-流水號），仍允許手動覆寫。
       * 它是 req，卻收在補充資料裡：見檔案上方「必填欄位進收合區」那段，選的是 (b)。
       */
      {
        key: "code",
        label: "專案代碼",
        type: "text",
        req: true,
        placeholder: "系統自動產生，可自行覆寫",
        hint: "由系統自動產生，通常無須修改。",
      },
      /*
       * 沒有「客戶名稱 / 狀態 / 優先級」三格，理由與下面的起訖日同一套：開案當下填的值沒人回頭改。
       *
       * 狀態：列表顯示的狀態本來就是算出來的 —— 工項全部結案就顯示「已完成」，
       *       有人開工就是「進行中」（見 ProjectService.ProjectList）。手動選一個
       *       只會做出「工項全做完、狀態還停在規劃中」這種卡片。
       *       省略時後端取 ProjectStatusEnum.Planning（規劃中），新開的案子本來就是這樣。
       * 優先級：省略時後端取 ProjectPriorityEnum.Low（0）。
       * 客戶名稱：留空時畫面顯示「內部」（見 design/domain.js normalizeProject）。
       */
      /*
       * 專案沒有「開始日 / 結束日」欄位是刻意的：時間由工項管，專案不管。
       *
       * 手填的專案日期沒人回頭改 —— 工項一路往後延，專案還停在開案當天填的
       * 那個結束日，儀表板的 RAG 就拿一個假的工期去算落後。
       * 現在專案卡與甘特圖顯示的起訖，是後端由底下工項推導出來的
       * （最早開始 ~ 最晚截止），要調時程請調工項。
       */
      {
        key: "git_url",
        label: "程式碼位置（Git 連結）",
        type: "text",
        full: true,
        placeholder: "https://git.lifeplus.net.tw/群組或主 repo",
        hint: "供程式碼檢測使用，可暫不填寫。",
      },
    ],
  };
  return c[ctx] || c.items;
}

/** 標題對照 — 對應設計稿 titleMap（L1212） */
export const FORM_TITLES = {
  items: "新增工項",
  phases: "新增里程碑",
  meetings: "新增會議紀錄",
  docs: "上傳文件",
  members: "新增成員",
  project: "新增專案",
};

export const FORM_EDIT_TITLES = {
  items: "編輯工項",
  phases: "編輯里程碑",
  meetings: "編輯會議紀錄",
  docs: "編輯文件",
  members: "編輯成員",
  project: "編輯專案",
};
